/**
 * Guía: Colaboradores.
 *
 * Contenido derivado del capítulo **11** del manual: §0 (las tres cosas que
 * separan este módulo de un catálogo), §2.1 (los siete roles), §4 (las
 * acciones masivas y lo que se saltan), §5 (el alta y el correo de
 * bienvenida) y §8.4 (por qué el borrado tiene guarda).
 *
 * Los dos pasos que más importan son los que avisan de lo irreversible: un
 * alta es una credencial nueva en producción, y asignar el rol de asesor
 * comercial provisiona un perfil que **comisiona sobre ventas**. Ninguna de
 * las dos cosas se ve mirando el formulario.
 */

import type { TourFlow } from '../types';

const STAFF_PATH = '/staff';

export const c11Colaboradores: TourFlow = {
  id: 'admin.staff',
  chapter: '11',
  title: 'Colaboradores',
  summary:
    'El equipo interno: por qué un alta es una credencial en producción, qué rol arrastra dinero y por qué no se borra.',
  href: STAFF_PATH,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'El equipo interno de 121Pass',
      body:
        'Las personas que entran a este panel. No son clientes de la app ni operadores de ' +
        'un comercio: son una tercera identidad, con correo corporativo y acceso por ' +
        'código de un solo uso.',
      route: STAFF_PATH,
    },
    {
      id: 'creating-is-credentials',
      title: 'Dar de alta es crear una credencial en producción',
      body:
        'No es rellenar una ficha. El rol que elijas decide qué puede tocar esa persona en ' +
        'toda la plataforma, desde el primer momento. Y el correo de bienvenida sale al ' +
        'buzón corporativo, así que un correo mal escrito deja un acceso creado y a nadie ' +
        'avisado.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'advisor-role-is-money',
      title: 'Un rol arrastra dinero',
      body:
        'Asignar «asesor comercial» no da sólo permisos: crea su perfil de asesor con su ' +
        'código público y su porcentaje de comisión sobre las ventas de los comercios que ' +
        'capte. Quitarle el rol lo desactiva, sin borrarle lo ganado.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'stats',
      title: 'El equipo de un vistazo',
      body:
        'Cuántos hay, cuántos activos y cómo se reparten. Sirve sobre todo para lo mismo ' +
        'que la lista de abajo: encontrar a alguien y ver si su acceso sigue vivo.',
      target: 'admin.staff.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'filters',
      title: 'Buscar por rol, país o estado',
      body:
        'El contador de resultados es del filtro. Ojo con el país: cada rol tiene su regla ' +
        '—el administrador general no tiene país porque los ve todos—, así que filtrar por ' +
        'país esconde precisamente a quien no lo tiene.',
      target: 'admin.staff.filters',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'deactivate-not-delete',
      title: 'El ciclo es crear, operar y desactivar',
      body:
        'Desactivar cierra el acceso y conserva todo lo que esa persona hizo. Es la vía ' +
        'normal cuando alguien deja el equipo: su historial tiene que sobrevivir, porque de ' +
        'él cuelgan tickets, decisiones y aprobaciones.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'delete-is-real',
      title: 'Borrar es real, y por eso está protegido',
      body:
        'No hay borrado lógico: borrar elimina la ficha de verdad. Por eso hay una guarda ' +
        'que lo impide en cuanto el colaborador dejó rastro. Queda reservado a deshacer un ' +
        'alta equivocada, que es el único caso en que no destruye nada.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'transfer-tickets',
      title: 'Al desactivar a un agente, sus tickets se transfieren',
      body:
        'El diálogo ofrece pasárselos a otra persona antes de cerrarle el acceso. Si no se ' +
        'hace, quedan asignados a alguien que ya no entra — y nadie los ve en su bandeja. ' +
        'Con muchos tickets abiertos, conviene comprobar después que no quedó ninguno.',
      roles: ['SUPER_ADMIN'],
    },
    {
      id: 'bulk',
      title: 'Las acciones masivas se saltan pasos',
      body:
        'Desactivar en bloque no pasa por la transferencia de tickets, y cambiar el rol en ' +
        'bloque rebota si el destino es finanzas o administrador general. El resumen dirá ' +
        '«N fallaron» sin decir por qué: para esos casos, uno a uno.',
      target: 'admin.staff.bulk',
      placement: 'top',
      optionalTarget: true,
      roles: ['SUPER_ADMIN'],
    },
  ],
};
